import { useEffect, useMemo, useRef, useState } from 'react'

export const useAccordionAnimations = (duration = 0, delay = 0, initialOpen = false) => {
    const animationOptions = { duration: duration * 1000, easing: 'ease-in', delay: delay * 1000 }
    const [mounted, setMounted] = useState(false)
    useEffect(() => setMounted(true), [])
    const [classMode, setClassMode] = useState<'open' | 'close'>('close')

    const detailRef = useRef<HTMLDetailsElement>(null)
    const animationRef = useRef<Animation | undefined>(undefined)
    const transitionRef = useRef<{ isOpening: boolean; isClosing: boolean }>({ isOpening: false, isClosing: false })

    useEffect(() => {
        if (!detailRef.current) return
        detailRef.current.open = initialOpen
        setClassMode(initialOpen ? 'open' : 'close')
    }, [])

    const elements = useMemo(() => {
        const detailElement = detailRef.current
        const summaryEl = detailElement?.querySelector<HTMLElement>('summary') ?? null
        const contentEl = detailElement?.querySelector<HTMLElement>('.accordion-plus__content') ?? null
        return { summaryEl, contentEl, detailElement }
    }, [mounted])

    const { summaryEl, contentEl, detailElement } = elements

    // opening accordion
    const openAccordion = () => {
        if (!detailElement) return
        const toHeight = `${(contentEl?.offsetHeight ?? 0) + (summaryEl?.offsetHeight ?? 0)}px`
        const fromHeight = `${detailElement.offsetHeight}px`

        detailElement.style.height = fromHeight
        detailElement.open = true // to display content before animation
        playAnimation('open', fromHeight, toHeight)
    }

    const closeAccordion = () => {
        console.log('close')
        if (!detailElement) return
        const toHeight = `${summaryEl?.offsetHeight ?? 0}px`
        const fromHeight = `${detailElement.offsetHeight}px`

        detailElement.style.height = fromHeight
        playAnimation('close', fromHeight, toHeight)
    }

    const playAnimation = (type: 'open' | 'close', startHeight: string, endHeight: string) => {
        const open = type === 'open'
        const tKey = open ? 'isOpening' : 'isClosing'
        const animation = animationRef.current
        setClassMode(type)

        transitionRef.current[tKey] = true
        if (animation) animation.cancel()
        animationRef.current = detailElement?.animate({ height: [startHeight, endHeight] }, animationOptions)
        animationRef.current!.onfinish = () => onAnimationFinish(open)
        animationRef.current!.oncancel = () => (transitionRef.current[tKey] = false)
    }

    const onAnimationFinish = (open: boolean) => {
        if (!detailElement) return
        detailElement.style.height = ''
        detailElement.style.overflow = ''
        detailElement.open = open
        animationRef.current = undefined
        transitionRef.current = { isOpening: false, isClosing: false }
    }

    const toggleAccordion = () => {
        if (!detailElement) return
        const { isOpening } = transitionRef.current
        const canClose = detailElement.open || isOpening
        detailElement.style.overflow = 'hidden'
        canClose ? closeAccordion() : openAccordion()
    }
    return { detailRef, toggleAccordion, openAccordion, closeAccordion, classMode }
}
